// Historic trend: inventory on hand, unfulfilled demand, and stock held above
// target — one point per calendar month, averaged over that month's runs.
//
// Three rules this chart obeys, all inherited from the rest of the page:
//
// 1. A month with no runs is a GAP, not a zero. The line breaks there. Joining
//    across it would draw a slope through months nobody measured.
// 2. A month built from one run is marked hollow. It is a point estimate, not
//    an average, and run density varies enormously between customers.
// 3. The current month is absent by construction (excluded server-side), since
//    a partial month would dip at the right edge and read as a decline.
//
// Rendering follows the page's diagram convention: all lines first, then all
// labels, with the label layer stroked in the surface colour so it stays
// legible where it crosses a series.
import { useMemo, useState } from "react";
import { SnapCard, useSnapshotTokens, un, EM_DASH } from "./primitives";
import { TrendPoint, monthsSpanning } from "./trend_data";

type SeriesKey = "inventoryUnits" | "unfulfilledUnits" | "excessUnits";
type CountKey = "inventoryRunCount" | "unfulfilledRunCount" | "excessRunCount";

// Each measure carries its OWN denominator: a month of 5 runs where one
// reported excess has an excess averaged over 1, and the marker and tooltip
// must say 1 rather than inheriting the month's run total.
const SERIES: {
  key: SeriesKey;
  countKey: CountKey;
  label: string;
  tone: "blue" | "red" | "orange";
}[] = [
  {
    key: "inventoryUnits",
    countKey: "inventoryRunCount",
    label: "Inventory on hand",
    tone: "blue",
  },
  {
    key: "unfulfilledUnits",
    countKey: "unfulfilledRunCount",
    label: "Unfulfilled demand",
    tone: "red",
  },
  {
    key: "excessUnits",
    countKey: "excessRunCount",
    label: "Above target",
    tone: "orange",
  },
];

const W = 780;
const H = 240;
const PAD = { top: 16, right: 18, bottom: 30, left: 58 };

// The year appears on January and on the first tick. Without it a span
// crossing a year boundary reads "Nov Dec Jan Feb", and two years of history
// gives two indistinguishable Januaries.
const monthLabel = (month: string, showYear: boolean): string => {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    ...(showYear ? { year: "2-digit" } : {}),
    timeZone: "UTC",
  });
};

export const TrendChart = ({
  points,
  loading,
  loaded,
  unavailable,
}: {
  points: TrendPoint[];
  loading: boolean;
  loaded: boolean;
  unavailable: boolean;
}) => {
  const t = useSnapshotTokens();
  const [hidden, setHidden] = useState<Set<SeriesKey>>(new Set());
  const color = { blue: t.blue, red: t.redInk, orange: t.orangeInk };

  const months = useMemo(() => monthsSpanning(points), [points]);
  const byMonth = useMemo(
    () => new Map(points.map((p) => [p.month, p])),
    [points]
  );

  const visible = SERIES.filter((s) => !hidden.has(s.key));
  const max = useMemo(() => {
    let m = 0;
    for (const p of points) {
      for (const s of visible) {
        const v = p[s.key];
        if (v != null && v > m) {
          m = v;
        }
      }
    }
    return m;
    // Depend on `hidden`: `visible` is rebuilt every render, so depending on
    // it would defeat the memo entirely.
  }, [points, hidden]);

  if (loading) {
    return (
      <SnapCard pad={16}>
        <span style={{ fontSize: 12.5, color: t.ink3 }}>Loading trend…</span>
      </SnapCard>
    );
  }

  // A failed request is NOT an empty series. Saying "no months recorded yet"
  // would state a fact about the customer's history on the strength of a
  // request that never arrived.
  if (unavailable) {
    return (
      <SnapCard pad={16}>
        <span style={{ fontSize: 12.5, color: t.ink3 }}>
          Trend unavailable — the series could not be loaded. This says nothing
          about the run history; try reloading.
        </span>
      </SnapCard>
    );
  }

  // An empty series is the normal state until runs accumulate. Say why rather
  // than showing an empty grid, which reads as a broken chart.
  if (loaded && points.length === 0) {
    return (
      <SnapCard pad={16}>
        <span style={{ fontSize: 12.5, color: t.ink3 }}>
          No completed months recorded yet. A point appears once a calendar
          month has finished with at least one run in it.
        </span>
      </SnapCard>
    );
  }

  // Hiding every series (or an all-zero month) leaves max at 0, which would
  // put all three gridlines and all three labels on the same baseline pixel.
  // The scale gets a floor; `max` still decides whether the labels read as
  // real numbers or as em dashes.
  const scaleMax = max > 0 ? max : 1;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) =>
    PAD.left + (months.length === 1 ? plotW / 2 : (i / (months.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / scaleMax) * plotH;

  /** Contiguous runs of months that have a value, so gaps break the line. */
  const segmentsFor = (key: SeriesKey): { i: number; v: number }[][] => {
    const segments: { i: number; v: number }[][] = [];
    let current: { i: number; v: number }[] = [];
    months.forEach((month, i) => {
      const v = byMonth.get(month)?.[key];
      if (v == null) {
        if (current.length) {
          segments.push(current);
        }
        current = [];
        return;
      }
      current.push({ i, v });
    });
    if (current.length) {
      segments.push(current);
    }
    return segments;
  };

  const ticks = [0, 0.5, 1].map((f) => scaleMax * f);

  return (
    <SnapCard pad={16}>
      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 6,
        }}
      >
        {SERIES.map((s) => {
          const off = hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={!off}
              onClick={() =>
                setHidden((prev) => {
                  const next = new Set(prev);
                  next.has(s.key) ? next.delete(s.key) : next.add(s.key);
                  return next;
                })
              }
              style={{
                appearance: "none",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                font: "inherit",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11.5,
                color: off ? t.ink4 : t.ink2,
                opacity: off ? 0.55 : 1,
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 3,
                  borderRadius: 2,
                  background: off ? t.ink4 : color[s.tone],
                }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label="Monthly trend of inventory, unfulfilled demand and stock above target"
      >
        {/* Grid and axis first — everything else draws over it. */}
        {ticks.map((v, i) => (
          <line
            key={i}
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(v)}
            y2={y(v)}
            stroke={t.line2}
            strokeWidth="1"
          />
        ))}

        {visible.map((s) =>
          segmentsFor(s.key).map((seg, si) => (
            <polyline
              key={`${s.key}-${si}`}
              points={seg.map((p) => `${x(p.i)},${y(p.v)}`).join(" ")}
              fill="none"
              stroke={color[s.tone]}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))
        )}

        {visible.map((s) =>
          months.map((month, i) => {
            const point = byMonth.get(month);
            const v = point?.[s.key];
            if (v == null) {
              return null;
            }
            // Hollow marker = a single run behind THIS measure, which is not
            // necessarily the month's run count.
            const measureRuns = point![s.countKey];
            const thin = measureRuns < 2;
            return (
              <circle
                key={`${s.key}-${month}`}
                cx={x(i)}
                cy={y(v)}
                r={thin ? 3.4 : 2.8}
                fill={thin ? t.surface : color[s.tone]}
                stroke={color[s.tone]}
                strokeWidth={thin ? 1.6 : 0}
              >
                <title>
                  {`${month} · ${s.label}: ${un(v)} units · averaged over ${measureRuns} ${
                    measureRuns === 1 ? "run" : "runs"
                  }${
                    measureRuns === point!.runCount
                      ? ""
                      : ` of the month's ${point!.runCount}`
                  }`}
                </title>
              </circle>
            );
          })
        )}

        {/* Label layer last, stroked in the surface colour so it stays legible
            where it crosses a series. */}
        <g style={{ paintOrder: "stroke" }}>
          {ticks.map((v, i) => (
            <text
              key={i}
              x={PAD.left - 8}
              y={y(v) + 3.5}
              textAnchor="end"
              fontSize="10"
              fill={t.ink4}
              stroke={t.surface}
              strokeWidth="3"
            >
              {max > 0 ? un(v) : EM_DASH}
            </text>
          ))}
          {months.map((month, i) => {
            // Thin the axis when months would collide. The last tick is always
            // drawn, so drop the strided one immediately before it — otherwise
            // the two sit a single slot apart and overlap.
            const step = Math.ceil(months.length / 12);
            const last = months.length - 1;
            const strided = i % step === 0;
            if (!strided && i !== last) {
              return null;
            }
            if (strided && i !== last && last - i < step) {
              return null;
            }
            return (
              <text
                key={month}
                x={x(i)}
                y={H - 10}
                textAnchor="middle"
                fontSize="10"
                fill={byMonth.has(month) ? t.ink4 : t.ink5}
                stroke={t.surface}
                strokeWidth="3"
              >
                {monthLabel(month, i === 0 || month.endsWith("-01"))}
              </text>
            );
          })}
        </g>
      </svg>

      <div style={{ fontSize: 11, color: t.ink4, marginTop: 4 }}>
        Averaged per month across that month's runs. Hollow points are months
        with a single run. Months with no runs are left blank.
      </div>
    </SnapCard>
  );
};
